<template>
    <div
        class="ui-media-list"
        @dragover.prevent
        @drop.prevent="handleDrop"
    >
        <div v-if="media.length" class="ui-media-list__wrapper">
            <div
                v-for="(mediaItem, index) in media"
                :key="mediaItem.id || index"
                class="ui-media-list__item"
            >
                <div class="ui-media-list__item-image">
                    <img v-svg-inline :src="getIconByFileType(mediaItem.file?.type ?? mediaItem.type)" />
                </div>

                <div class="ui-media-list__item-info">
                    <div class="ui-media-list__item-name">{{ mediaItem.file?.name || mediaItem.label }}</div>
                    <div class="ui-media-list__item-size">{{ getMediaItemSize(mediaItem.file) }}</div>
                </div>

                <div class="ui-media-list__item-actions">
                    <div class="ui-media-list__item-action delete" @click="removeMedia(index)">
                        <img v-svg-inline src="@/assets/icons/ui/close.svg" />
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="maxFiles && media.length < maxFiles"
            class="ui-media-list__action"
        >
            <div class="ui-media-list__action-title">Перетащите сюда или Выберите файлы</div>

            <div class="ui-media-list__action-button">
                <UISimpleButton
                    button-type="outlined"
                    @clicked="openFileDialog"
                >
                    <template #icon>
                        <img v-svg-inline src="@/assets/icons/ui/plus.svg" />
                    </template>
                    Выбрать файл
                </UISimpleButton>
            </div>

            <input
                ref="fileInput"
                type="file"
                multiple
                :accept="computedAccept"
                style="display: none;"
                @change="handleFileInput"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import PdfFileIcon from '@/assets/icons/file-pdf.svg';
    import JpegFileIcon from '@/assets/icons/file-jpeg.svg';
    import JpgFileIcon from '@/assets/icons/file-jpg.svg';
    import PngFileIcon from '@/assets/icons/file-png.svg';
    import WebpFileIcon from '@/assets/icons/file-webp.svg';
    import VideoFile from '@/assets/icons/file-video.svg';
    import DocFileIcon from '@/assets/icons/file-doc.svg';
    import DocxFileIcon from '@/assets/icons/file-docx.svg';
    import XlsFileIcon from '@/assets/icons/file-xls.svg';
    import XlsxFileIcon from '@/assets/icons/file-xlsx.svg';
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';

    import { ref, watch, defineProps, defineEmits, computed } from 'vue';

    import { MediaItem } from '@/types/form.type.ts';

    import useNotifications from '@/utils/useNotifications.ts';

    import { formatFileSize } from '@/utils/NumberFormatter.ts';

    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const props = defineProps({
        initialMedia: {
            type: Array as () => MediaItem[],
            default: () => [],
        },
        allowedFileTypes: {
            type: Array,
            default: () => [],
        },
        maxFiles: {
            type: Number,
            default: null,
        },
    });

    const emit = defineEmits(['media-updated']);

    const { showError } = useNotifications();

    const media = ref([...props.initialMedia]);
    const fileInput = ref<HTMLInputElement | null>(null);

    const localAllowedFileTypes = props.allowedFileTypes.flatMap(allowedFileType => {
        if (allowedFileType === 'video/*') {
            return ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'];
        }
        else if (allowedFileType === 'image/*') {
            return ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        }

        return allowedFileType;
    });

    const computedAccept = computed(() => (localAllowedFileTypes.length ? localAllowedFileTypes.join(',') : ''));

    watch(() => props.initialMedia, (newVal) => (media.value = [...newVal]), { deep: true, immediate: true });

    function removeMedia(index: number): void {
        media.value.splice(index, 1);
        emitMediaUpdated();
    }

    function openFileDialog(): void {
        fileInput.value?.click();
    }

    function handleFileInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }

        Array.from(input.files).forEach((file) => acceptFile(file) && addMedia(file));
        input.value = '';
    }

    function handleDrop(e: DragEvent): void {
        if (!e.dataTransfer?.files?.length) {
            return;
        }

        Array.from(e.dataTransfer.files).forEach((file) => acceptFile(file) && addMedia(file));
    }

    function acceptFile(file: File): boolean {
        const acceptedFile = localAllowedFileTypes.length !== 0 && localAllowedFileTypes.includes(file.type);

        if (!acceptedFile) {
            showError({
                message: 'Не поддерживаемый формат файла',
            });
        }

        return acceptedFile;
    }

    function addMedia(file: File): void {
        media.value.push({
            id: generateUUIDv4(),
            type: file.type,
            file,
            label: file.name,
            previewUrl: '',
            rotation: 0,
        });

        emitMediaUpdated();
    }

    function generateUUIDv4(): string {
        return Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
    }

    function emitMediaUpdated(): void {
        emit('media-updated', media.value);
    }

    function getIconByFileType(fileType?: string): string {
        if (!fileType) {
            return noPhotoImage;
        }

        if (fileType === 'application/pdf') {
            return PdfFileIcon;
        }
        else if (fileType === 'image/jpeg') {
            return JpegFileIcon;
        }
        else if (fileType === 'image/jpg') {
            return JpgFileIcon;
        }
        else if (fileType === 'image/png') {
            return PngFileIcon;
        }
        else if (fileType === 'image/webp') {
            return WebpFileIcon;
        }
        else if (fileType.startsWith('video/')) {
            return VideoFile;
        }
        else if (fileType === 'application/msword') {
            return DocFileIcon;
        }
        else if (fileType === 'application/vnd.ms-excel') {
            return XlsFileIcon;
        }
        else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return DocxFileIcon;
        }
        else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            return XlsxFileIcon;
        }

        return noPhotoImage;
    }

    function getMediaItemSize(file?: File | null): string {
        return file ? formatFileSize(file.size) : '';
    }
</script>

<style lang="scss" scoped>
    @use './UIMediaList.scss' as *;
</style>
